/**
 * BrandingCard — Firmenname + Logo-Upload.
 */
import React, { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, Upload, Trash2, Loader2, Image } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const buildLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

export const BrandingCard = ({ formData, onChange, onLogoUpload, onLogoDelete, uploadingLogo }) => {
  const fileInputRef = useRef(null);

  const handleFile = (e) => {
    onLogoUpload(e);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          Firmenidentität
        </CardTitle>
        <CardDescription>Name und Logo Ihrer Firma</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Firmenname *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Meine Firma GmbH"
            data-testid="company-name-input"
          />
        </div>

        <div className="space-y-3">
          <Label>Firmenlogo</Label>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-shrink-0">
              <div className="w-32 h-32 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-secondary/20 overflow-hidden">
                {formData.logo_url ? (
                  <img
                    src={buildLogoUrl(formData.logo_url)}
                    alt="Firmenlogo"
                    className="max-w-full max-h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      const errorSpan = document.createElement('span');
                      errorSpan.className = 'text-xs text-muted-foreground text-center px-2';
                      errorSpan.textContent = 'Bild konnte nicht geladen werden';
                      parent.appendChild(errorSpan);
                    }}
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Image className="h-8 w-8 mx-auto mb-1 opacity-50" />
                    <span className="text-xs">Kein Logo</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleFile}
                className="hidden"
                id="logo-upload"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="rounded-full"
                  data-testid="upload-logo-btn"
                >
                  {uploadingLogo ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird hochgeladen...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" />Logo hochladen</>
                  )}
                </Button>
                {formData.logo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onLogoDelete}
                    className="rounded-full text-destructive hover:text-destructive"
                    data-testid="delete-logo-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Löschen
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">PNG, JPG, SVG oder WebP • Max. 2MB</p>

              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Oder URL eingeben
                </summary>
                <div className="mt-2">
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => onChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="text-sm"
                  />
                </div>
              </details>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
